// src/components/.../AdminDataErrorView.tsx
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Stack, useTheme, Box, Typography } from "@mui/material";
import { ThemeProvider } from "styled-components";
import { gridTheme } from "../../../utils/tableUtils";
import { setSelectedRow } from "../../../redux/actions/supportTicket";
import { useDispatch, useSelector } from "react-redux";

import { buildAdminErrorColumns, buildAdminErrorRows } from "./SupportSummary";

import { SupportTicketSummaryApi } from "../../../types/supportTicket";
import { fetchSupportTicketSummary } from "../../../api/supportTicket";
import Download from "../../../components/common/DownloadGrid/Download";
import { RenderTable } from "../../../components/common/splitview-table/RenderTable";
import { setApiCall } from "./../../../redux/actions/userSlice";
import ButtonComponent from "../../../components/common/button/Button";
import FilterManagerButton from "../../../components/common/FilterVariant/FilterManagerButton";

const AdminSupportTicketSummary = () => {
  const theme = useTheme();
  const [source, setSource] = useState<SupportTicketSummaryApi[]>([]);
  const [loading, setLoading] = useState(false);
  const selectedRowIdsRef = useRef<Set<string>>(new Set());
  const dropdownValuesSetRef = useRef(false);
  const gridRef = useRef<any>(null);
  const prevPageRef = useRef<number | null>(null);
  const lastFetchedPlantKeyRef = useRef<string | null>(null);
  const dispatch = useDispatch();
  const selectedPlant = useSelector((state: any) => state.userRoles.selectedPlant || []);

  const { isSelectionRemove } = useSelector(
    (state: any) => state.supportTicket,
  );

  // --- data fetch (unchanged) ---
  useEffect(() => {
   if (!selectedPlant || selectedPlant.length === 0) {
    setSource([]);
    lastFetchedPlantKeyRef.current = null;
    
    return;
  }

      const plantKey = selectedPlant.join(",");
      if (lastFetchedPlantKeyRef.current === plantKey) {
        return;
      }

      lastFetchedPlantKeyRef.current = plantKey;
      fetchData();
    
  }, [selectedPlant]);
  const fetchData = async () => {
    setLoading(true);
    dispatch(setApiCall(true));
    try {
      const data: any = await fetchSupportTicketSummary();
      setSource(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
      dispatch(setApiCall(false));
    }
  };
  // --- columns & rows from helpers ---
  const columns = useMemo(() => buildAdminErrorColumns(), []);
  const rows = useMemo(
    () => buildAdminErrorRows(source, columns),
    [source, columns],
  );

  const handleSelectionChanged = useCallback(
    (event: any) => {
      const nodes = event.api.getSelectedNodes();
      selectedRowIdsRef.current = new Set(nodes.map((n: any) => String(n.id)));
      dispatch(setSelectedRow(nodes.map((n: any) => n.data)));
    },
    [dispatch],
  );

  const handlePageChange = useCallback(() => {
    const api = gridRef?.current?.api;
    if (!api) return;
    const current = api.paginationGetCurrentPage?.() ?? 0;
    const prev = prevPageRef.current;
    if (prev === null) {
      prevPageRef.current = current;
      return;
    }
    if (current !== prev) {
      api.deselectAll();
      selectedRowIdsRef.current = new Set();
      dropdownValuesSetRef.current = false;
      prevPageRef.current = current;
    }
  }, [gridRef]);

  const deSelectItems = () => {
    const api = gridRef.current?.api;
    if (api) api.deselectAll();
  };

  useEffect(() => {
    if (!isSelectionRemove) return;
    deSelectItems();
  }, [isSelectionRemove]);

  const handleResetFilters = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    api.onFilterChanged();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Stack gap={2} padding={2} sx={{ padding: 2, paddingLeft: 0, flex: 1 }}>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
            Support Ticket Summary
          </Typography>
          {/* Right-side controls */}
          <Stack direction="row" spacing={1} alignItems="center">
            {(rows?.length ?? 0) > 0 && (
              <><ButtonComponent
                className="take-action"
                  type="secondary"
                label="Reset Filters"
                onClick={handleResetFilters}
                borderRadius="28px"
                width="auto"
                maxWidth="none"
              />
               <FilterManagerButton    
               gridRef={gridRef}    
               tableName="Support Ticket Summary"    
               userEmail={localStorage.getItem("userEmail") || ""}    
               disabled={!source?.length}  /></>
            )}
            <Download
              loading={loading}
              gridRef={gridRef}
              fileName="Support Ticket Summary"
              rowData={rows}
            />
          </Stack>
        </Stack>

        <RenderTable
          loading={loading}
          gridRef={gridRef}
          columns={columns}
          gridTheme={gridTheme}
          rows={rows}
          rowsPerPage={10}
          setGridReady={() => {}}
          sideBar={{}}
          minimized={null}
          emitEvent={() => {}}
          onChange={() => {}}
          setSelectedRow={() => {}}
          setOpenRejectPopup={() => {}}
          withSelectionColumn={true}
          selectionScope="page"
          onSelectionChanged={handleSelectionChanged}
          handleChangePage={handlePageChange}
          isSelectedRow={false}
        />
        <Box paddingBottom={1}></Box>
      </Stack>
    </ThemeProvider>
  );
};
export default AdminSupportTicketSummary;
